
import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';
import { Note, NoteRelevance } from '@shared/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PlusIcon, Search, Filter, X, FileText, AlertCircle, Star } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { LoadingState } from '@/components/ui/loading-state';
import { NoteCard } from '@/components/notas/NoteCard';
import { ViewNoteDialog } from '@/components/notas/ViewNoteDialog';
import { NoteFormDialog } from '@/components/notas/NoteFormDialog';

const relevanceOptions: { value: NoteRelevance; label: string; icon: typeof FileText }[] = [
  { value: 'normal', label: 'Normal', icon: FileText },
  { value: 'importante', label: 'Importante', icon: AlertCircle },
  { value: 'destacada', label: 'Destacada', icon: Star },
];

export default function Notas() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [currentNote, setCurrentNote] = useState<Partial<Note> | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewNote, setViewNote] = useState<Note | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const { fetchWithAuth } = useUser();

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [relevanceFilter, setRelevanceFilter] = useState<NoteRelevance | 'all'>('all');
  const debouncedSearch = useDebounce(searchTerm, 300);

  const fetchNotes = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth('/api/notas');
      if (!response.ok) throw new Error('Error al cargar notas.');
      const data = await response.json();
      setNotes(data);
    } catch (error) {
      toast.error('No se pudieron cargar las notas.');
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Filtered notes
  const filteredNotes = useMemo(() => {
    return notes.filter(note => {
      const matchesSearch = debouncedSearch === '' ||
        note.title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        note.content.toLowerCase().includes(debouncedSearch.toLowerCase());

      const matchesRelevance = relevanceFilter === 'all' || note.relevance === relevanceFilter;

      return matchesSearch && matchesRelevance;
    });
  }, [notes, debouncedSearch, relevanceFilter]);

  const hasActiveFilters = searchTerm !== '' || relevanceFilter !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setRelevanceFilter('all');
  };

  const handleNew = () => {
    setCurrentNote({ title: '', content: '', relevance: 'normal', adjuntos: [] });
    setDialogOpen(true);
  };

  const handleEdit = (note: Note) => {
    setCurrentNote(note);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!currentNote || !currentNote.title?.trim() || !currentNote.content?.trim()) {
      toast.error('El título y el contenido son obligatorios.');
      return;
    }

    const apiPath = currentNote.id ? `/api/notas/${currentNote.id}` : '/api/notas';
    const method = currentNote.id ? 'PUT' : 'POST';

    const promise = fetchWithAuth(apiPath, {
      method,
      body: JSON.stringify({
        title: currentNote.title,
        content: currentNote.content,
        relevance: currentNote.relevance,
      }),
    });

    toast.promise(promise, {
      loading: 'Guardando nota...',
      success: async () => {
        await fetchNotes();
        setDialogOpen(false);
        setCurrentNote(null);
        return `Nota ${currentNote.id ? 'actualizada' : 'creada'} con éxito.`;
      },
      error: (err) => `No se pudo guardar la nota: ${err.message}`,
    });
  };

  const handleDeleteConfirm = () => {
    if (!deleteId) return;
    const promise = fetchWithAuth(`/api/notas/${deleteId}`, { method: 'DELETE' });
    toast.promise(promise, {
      loading: 'Eliminando nota...',
      success: () => {
        fetchNotes();
        setDeleteId(null);
        return 'Nota eliminada.';
      },
      error: 'No se pudo eliminar la nota.',
    });
  };

  const handleFileUpload = async (noteId: string, file: globalThis.File) => {
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo es demasiado grande. Máximo 10MB.');
      return;
    }

    setIsUploading(true);
    try {
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(file);
      const fileBase64 = await base64Promise;

      const response = await fetchWithAuth(`/api/notas/${noteId}/adjuntos`, {
        method: 'POST',
        body: JSON.stringify({
          fileBase64,
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error al subir archivo');
      }

      toast.success('Archivo adjuntado correctamente');
      await fetchNotes();

      // Update viewNote if we're viewing this note
      if (viewNote?.id === noteId) {
        const updatedNotes = await (await fetchWithAuth('/api/notas')).json();
        const updatedNote = updatedNotes.find((n: Note) => n.id === noteId);
        if (updatedNote) setViewNote(updatedNote);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al subir archivo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteAttachment = async (noteId: string, attachmentIndex: number) => {
    try {
      const response = await fetchWithAuth(`/api/notas/${noteId}/adjuntos/${attachmentIndex}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Error al eliminar adjunto');
      }

      toast.success('Adjunto eliminado');
      await fetchNotes();

      // Update viewNote if we're viewing this note
      if (viewNote?.id === noteId) {
        const updatedNotes = await (await fetchWithAuth('/api/notas')).json();
        const updatedNote = updatedNotes.find((n: Note) => n.id === noteId);
        if (updatedNote) setViewNote(updatedNote);
      }
    } catch (error) {
      toast.error('Error al eliminar adjunto');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tighter">Notas</h1>
          <p className="text-muted-foreground mt-1">Crea y gestiona tus apuntes importantes.</p>
        </div>
        <Button onClick={handleNew}>
          <PlusIcon className="mr-2 h-4 w-4" /> Nueva Nota
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por título o contenido..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Select value={relevanceFilter} onValueChange={(val) => setRelevanceFilter(val as NoteRelevance | 'all')}>
                <SelectTrigger className="w-[160px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Relevancia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {relevanceOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        <opt.icon className="h-4 w-4" />
                        {opt.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant="ghost" size="icon" onClick={clearFilters} title="Limpiar filtros">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          {hasActiveFilters && (
            <p className="text-sm text-muted-foreground mt-2">
              Mostrando {filteredNotes.length} de {notes.length} notas
            </p>
          )}
        </CardContent>
      </Card>

      {/* Notes Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          <div className="col-span-full">
            <LoadingState message="Cargando notas..." />
          </div>
        ) : filteredNotes.length > 0 ? (
          filteredNotes.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              onView={setViewNote}
              onEdit={handleEdit}
              onDelete={setDeleteId}
            />
          ))
        ) : (
          <div className="col-span-full">
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                {hasActiveFilters
                  ? 'No hay notas que coincidan con los filtros.'
                  : 'No has creado ninguna nota todavía.'}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <ViewNoteDialog
        note={viewNote}
        onClose={() => setViewNote(null)}
        onEdit={handleEdit}
        onFileUpload={handleFileUpload}
        onDeleteAttachment={handleDeleteAttachment}
        isUploading={isUploading}
      />

      <NoteFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        note={currentNote}
        onNoteChange={setCurrentNote}
        onSave={handleSave}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={(isOpen) => !isOpen && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta nota?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer y eliminará la nota y todos sus adjuntos de forma permanente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Confirmar y Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
